
import React from 'react';
import Link from 'next/link';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { getNestedValue, hasRenderableValue } from '../utils/dataBinding';
import { resolveCustomThemeHref } from '../utils/linkHelpers';
import { resolveStaticPageValue } from '../utils/resolveStaticPageData';
import { buildBreadcrumbCrumbs } from '../renderer';

interface HeadingComponentProps {
    component: ComponentNode;
    data?: any;
    context?: {
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
        staticPageData?: any;
        basePath?: string;
        params?: Record<string, any>;
    };
}

const HeadingComponent: React.FC<HeadingComponentProps> = ({ component, data, context }) => {
    const { data: componentData, placeholder, styles, type, href, customClassName, hideIfEmpty } = component;
    const inlineStyles = convertStylesToInline(styles);

    // Some API-driven slides have no real caption at all (e.g. the banner image
    // already has text baked in) - rather than show the template's static
    // placeholder text on the live page, skip rendering entirely.
    if (hideIfEmpty && !hasRenderableValue(getNestedValue(data, hideIfEmpty))) {
        return null;
    }

    const dynamicValue = placeholder
        ? getNestedValue(data, placeholder) ?? resolveStaticPageValue(placeholder, context?.staticPageData)
        : undefined;
    const resolvedTextValue =
        typeof dynamicValue === 'string'
            ? dynamicValue.trim() || componentData
            : dynamicValue ?? componentData;
    // The heading renderer handles H1 nodes (TextComponent does not).
    // Derive only the default title from the current route; saved custom text wins.
    const routeTitle =
        type === 'H1' && resolvedTextValue === 'Page title'
            ? buildBreadcrumbCrumbs(context)?.at(-1)?.label
            : undefined;
    const textValue = routeTitle || resolvedTextValue;
    const HeadingTag = type.toLowerCase() as 'h1' | 'h2' | 'h3' | 'h4';
    const resolvedHref = resolveCustomThemeHref(href, data, context);

    // page_description is a rich-text article from the footer-pages API, not plain
    // text - render it as actual HTML rather than as the heading's text content.
    if (placeholder === 'page_description') {
        return React.createElement(HeadingTag, {
            style: inlineStyles,
            className: `prose max-w-none ${customClassName || ''}`,
            dangerouslySetInnerHTML: { __html: textValue || '' },
        });
    }

    if (resolvedHref) {
        return (
            <Link href={resolvedHref}>
                {React.createElement(HeadingTag, { style: inlineStyles, className: customClassName }, textValue)}
            </Link>
        );
    }

    return React.createElement(HeadingTag, { style: inlineStyles, className: customClassName }, textValue);
};

export default HeadingComponent;
